import { useTranslation } from "react-i18next";
import { NavLink, useLocation } from "react-router-dom";
import { useState, useEffect, useLayoutEffect, useRef } from "react";
import { ChevronDown, Menu, X } from "lucide-react";

const products = [{ key: "skyglobalAi" }, { key: "secure" }, { key: "smartLiving" }];

const solutions = [
  { key: "skydial" },
  { key: "voiceapi" },
  { key: "aicounting" },
  { key: "vms" },
  { key: "soc" },
  { key: "vapt" },
  { key: "firstprotect" },
  { key: "networking" },
];

const LANGS = ["en", "id"];

const Navbar = () => {
  const { t, i18n } = useTranslation();
  const [openProducts, setOpenProducts] = useState(false);
  const [openSolutions, setOpenSolutions] = useState(false);
  const [openLang, setOpenLang] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);

  const location = useLocation();
  const isHomePage = location.pathname === "/";
  const headerRef = useRef(null);
  const langButtonRef = useRef(null);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 10);
    handleScroll();
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const closeMenus = () => {
    setOpenProducts(false);
    setOpenSolutions(false);
    setOpenLang(false);
  };

  useLayoutEffect(() => {
    const handleClickOutside = (e) => {
      if (headerRef.current && !headerRef.current.contains(e.target)) closeMenus();
    };
    const handleFocusIn = (e) => {
      const tgt = e.target;
      if (!headerRef.current || headerRef.current.contains(tgt)) return;
      if (!headerRef.current.contains(document.activeElement)) closeMenus();
    };
    const handleKey = (e) => {
      if (e.key === "Escape") {
        closeMenus();
        setIsMobileMenuOpen(false);
      }
    };
    document.addEventListener("click", handleClickOutside);
    document.addEventListener("focusin", handleFocusIn);
    document.addEventListener("keydown", handleKey);
    return () => {
      document.removeEventListener("click", handleClickOutside);
      document.removeEventListener("focusin", handleFocusIn);
      document.removeEventListener("keydown", handleKey);
    };
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
    closeMenus();
  }, [location.pathname]);

  const changeLang = (lang) => {
    i18n.changeLanguage(lang);
    setOpenLang(false);
    langButtonRef.current?.focus();
  };

  const menuItemClass =
    "block px-4 py-2.5 text-sm text-gray-600 hover:text-textBlue hover:bg-brand-50 transition-colors no-underline focus-visible:bg-brand-50 focus-visible:text-textBlue focus-visible:outline-none";

  const linkClass =
    "inline-flex items-center text-sm font-medium text-textBlueDark hover:text-textBlue transition-colors duration-200 no-underline rounded-md px-1 py-0.5 focus-visible:ring-2 focus-visible:ring-textBlue focus-visible:ring-offset-2";

  const toggleButtonClass =
    "inline-flex items-center gap-1 text-sm font-medium text-textBlueDark hover:text-textBlue transition-colors duration-200 rounded-md px-1 py-0.5 focus-visible:ring-2 focus-visible:ring-textBlue focus-visible:ring-offset-2";

  return (
    <header
      ref={headerRef}
      className={`fixed top-0 left-0 w-full z-30 transition-[background-color,border-color,box-shadow] duration-300 ${
        isHomePage && !isScrolled
          ? "bg-transparent border-b border-transparent"
          : "bg-white/90 backdrop-blur-md border-b border-line"
      }`}
    >
      <div className="container-page flex items-center justify-between h-16">
        <NavLink
          to="/"
          aria-label="Sky Global — home"
          className="flex items-center no-underline rounded-md focus-visible:ring-2 focus-visible:ring-textBlue"
        >
          <img
            src="/skyglobal-removebg-preview.svg"
            alt="Sky Global"
            className="h-11 w-auto"
            width="150"
            height="44"
          />
        </NavLink>

        <nav aria-label="Primary" className="flex items-center gap-7 mobile:hidden">
          <NavLink to="/" end className={linkClass}>
            {t("navbar.home")}
          </NavLink>

          <div className="relative">
            <button
              type="button"
              aria-expanded={openProducts}
              aria-controls="nav-products"
              onClick={() => {
                setOpenProducts((v) => !v);
                setOpenSolutions(false);
                setOpenLang(false);
              }}
              className={toggleButtonClass}
            >
              {t("navbar.products")}
              <ChevronDown
                aria-hidden="true"
                className={`w-4 h-4 transition-transform duration-200 ${openProducts ? "rotate-180" : ""}`}
              />
            </button>
            <div
              id="nav-products"
              hidden={!openProducts}
              className="absolute left-0 mt-3 w-60 bg-white border border-line rounded-xl shadow-lift py-1.5 z-40"
            >
                {products.map((product) => (
                  <NavLink
                    key={product.key}
                    to={`/products/${product.key}`}
                    onClick={() => setOpenProducts(false)}
                    className={menuItemClass}
                  >
                    {t(`products.${product.key}.title`)}
                  </NavLink>
                ))}
            </div>
          </div>

          <div className="relative">
            <button
              type="button"
              aria-expanded={openSolutions}
              aria-controls="nav-solutions"
              onClick={() => {
                setOpenSolutions((v) => !v);
                setOpenProducts(false);
                setOpenLang(false);
              }}
              className={toggleButtonClass}
            >
              {t("navbar.solutions")}
              <ChevronDown
                aria-hidden="true"
                className={`w-4 h-4 transition-transform duration-200 ${openSolutions ? "rotate-180" : ""}`}
              />
            </button>
            <div
              id="nav-solutions"
              hidden={!openSolutions}
              className="absolute left-0 mt-3 w-64 bg-white border border-line rounded-xl shadow-lift py-1.5 z-40"
            >
                {solutions.map((solution) => (
                  <NavLink
                    key={solution.key}
                    to={`/solutions/${solution.key}`}
                    onClick={() => setOpenSolutions(false)}
                    className={menuItemClass}
                  >
                    {t(`solutions.${solution.key}.title`)}
                  </NavLink>
                ))}
            </div>
          </div>

          <NavLink to="/about" className={linkClass}>
            {t("navbar.about")}
          </NavLink>
        </nav>

        <div className="flex items-center gap-3 mobile:hidden">
          <div className="relative">
            <button
              ref={langButtonRef}
              type="button"
              onClick={() => {
                setOpenLang((v) => !v);
                setOpenProducts(false);
                setOpenSolutions(false);
              }}
              aria-expanded={openLang}
              aria-controls="nav-lang"
              className="inline-flex items-center gap-1.5 border border-line px-3 py-1.5 rounded-full text-xs font-medium text-textBlueDark hover:text-textBlue hover:border-textBlue transition-colors focus-visible:ring-2 focus-visible:ring-textBlue focus-visible:ring-offset-2"
            >
              {t("language.code." + i18n.language)}
              <ChevronDown aria-hidden="true" className="w-3.5 h-3.5" />
            </button>
            {openLang && (
              <ul
                id="nav-lang"
                className="absolute right-0 mt-2 w-40 bg-white border border-line rounded-xl shadow-lift py-1.5 z-40"
              >
                {LANGS.map((lang) => (
                  <li key={lang}>
                    <button
                      type="button"
                      lang={lang}
                      aria-current={i18n.language === lang ? "true" : undefined}
                      onClick={() => changeLang(lang)}
                      className="block w-full text-left px-4 py-2 text-sm text-gray-600 hover:bg-brand-50 hover:text-textBlue transition-colors focus-visible:bg-brand-50 focus-visible:text-textBlue focus-visible:outline-none"
                    >
                      {t("language." + lang)}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <NavLink
            to="/contact"
            className="inline-flex items-center rounded-full bg-textBlue text-white text-sm font-medium px-5 py-2 hover:bg-textBlueDark transition-colors no-underline focus-visible:ring-2 focus-visible:ring-textBlue focus-visible:ring-offset-2"
          >
            {t("navbar.contact")}
          </NavLink>
        </div>

        <div className="hidden tablet:flex">
          <button
            type="button"
            aria-label={isMobileMenuOpen ? t("navbar.menuClose") : t("navbar.menuOpen")}
            aria-expanded={isMobileMenuOpen}
            aria-controls="mobile-menu"
            onClick={() => setIsMobileMenuOpen((v) => !v)}
            className="p-2 -mr-2 rounded-md text-textBlueDark hover:bg-brand-50 transition-colors focus-visible:ring-2 focus-visible:ring-textBlue"
          >
            {isMobileMenuOpen ? (
              <X aria-hidden="true" className="w-6 h-6" />
            ) : (
              <Menu aria-hidden="true" className="w-6 h-6" />
            )}
          </button>
        </div>
      </div>

      {isMobileMenuOpen && (
        <nav
          id="mobile-menu"
          aria-label="Mobile"
          className="absolute top-full left-0 w-full bg-white border-b border-line shadow-lift flex flex-col py-4 px-6 gap-1 text-base max-h-[calc(100dvh-4rem)] overflow-y-auto overscroll-contain"
        >
          <NavLink
            to="/"
            end
            className="py-2.5 text-textBlueDark hover:text-textBlue no-underline transition-colors"
            onClick={() => setIsMobileMenuOpen(false)}
          >
            {t("navbar.home")}
          </NavLink>

          <div className="flex flex-col">
            <NavLink
              to="/products"
              className="py-2.5 text-textBlueDark hover:text-textBlue no-underline font-medium transition-colors"
              onClick={() => setIsMobileMenuOpen(false)}
            >
              {t("navbar.products")}
            </NavLink>
            <div className="flex flex-col pl-4 border-l border-line ml-1">
              {products.map((product) => (
                <NavLink
                  key={product.key}
                  to={`/products/${product.key}`}
                  className="py-2 text-sm text-gray-600 hover:text-textBlue no-underline transition-colors"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  {t(`products.${product.key}.title`)}
                </NavLink>
              ))}
            </div>
          </div>

          <div className="flex flex-col">
            <NavLink
              to="/solutions"
              className="py-2.5 text-textBlueDark hover:text-textBlue no-underline font-medium transition-colors"
              onClick={() => setIsMobileMenuOpen(false)}
            >
              {t("navbar.solutions")}
            </NavLink>
            <div className="flex flex-col pl-4 border-l border-line ml-1">
              {solutions.map((solution) => (
                <NavLink
                  key={solution.key}
                  to={`/solutions/${solution.key}`}
                  className="py-2 text-sm text-gray-600 hover:text-textBlue no-underline transition-colors"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  {t(`solutions.${solution.key}.title`)}
                </NavLink>
              ))}
            </div>
          </div>

          <NavLink
            to="/about"
            className="py-2.5 text-textBlueDark hover:text-textBlue no-underline transition-colors"
            onClick={() => setIsMobileMenuOpen(false)}
          >
            {t("navbar.about")}
          </NavLink>
          <NavLink
            to="/contact"
            className="py-2.5 text-textBlueDark hover:text-textBlue no-underline transition-colors"
            onClick={() => setIsMobileMenuOpen(false)}
          >
            {t("navbar.contact")}
          </NavLink>

          <div className="mt-3 pt-3 border-t border-line">
            <label htmlFor="lang-select" className="sr-only">
              {t("language.label")}
            </label>
            <select
              id="lang-select"
              value={i18n.language}
              onChange={(e) => changeLang(e.target.value)}
              className="w-[160px] border border-line rounded-lg py-2 px-3 text-sm bg-white text-textBlueDark"
            >
              {LANGS.map((lang) => (
                <option key={lang} value={lang}>
                  {t("language." + lang)}
                </option>
              ))}
            </select>
          </div>
        </nav>
      )}
    </header>
  );
};

export default Navbar;
